import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, FileText, PenLine, ChevronDown, ChevronRight } from "lucide-react";
import { fmtDate } from "@/lib/dates";

const UPLOADABLE = ["Assigned", "Rejected", "In Progress", "Not Started"];

export const SchoolStudentDocs = () => {
  const [groups, setGroups] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(null);
  const fileRefs = useRef({});

  const load = () => api.get("/school-paperwork").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const onFile = async (item, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(item.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("notes", "Uploaded by the school on the student's behalf");
      await api.post(`/paperwork-assignments/${item.id}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${item.name} sent to the Campus Coordinator for review`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); e.target.value = ""; }
  };

  if (groups === null) return null;

  return (
    <Card title="Student documents" data-testid="school-student-docs-card"
      action={<span className="text-xs text-zinc-500" data-testid="school-docs-count">{groups.length} student{groups.length === 1 ? "" : "s"}</span>}>
      <p className="text-sm text-zinc-500 mb-3">
        Track your students' clearance paperwork and upload completed forms on their behalf. The student and their
        Campus Coordinator are notified for every upload. Forms the student signs electronically stay with them.
      </p>
      {groups.length === 0 ? (
        <EmptyState text="No documents assigned to your students yet — the Campus Coordinator assigns a packet once a facility accepts." />
      ) : (
        <div className="space-y-2">
          {groups.map((g) => {
            const isOpen = open[g.student_id] !== false;
            return (
              <div key={g.student_id} className="border rounded-md" data-testid={`school-doc-group-${g.student_id}`}>
                <button className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors"
                  onClick={() => setOpen((o) => ({ ...o, [g.student_id]: !isOpen }))}
                  data-testid={`school-doc-toggle-${g.student_id}`}>
                  <span className="flex items-center gap-2 min-w-0 flex-1">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />}
                    <span className="text-sm font-medium text-zinc-800">{g.student_name}</span>
                    {g.discipline && <span className="text-xs text-zinc-400">{g.discipline}</span>}
                  </span>
                  <span className="text-xs shrink-0" data-testid={`school-doc-progress-${g.student_id}`}>
                    <span className="text-cc-green font-semibold">{g.approved}</span>
                    <span className="text-zinc-400">/{g.items.length} approved</span>
                    {g.outstanding > 0 && <span className="ml-2 text-cc-orange font-semibold">{g.outstanding} outstanding</span>}
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t divide-y">
                    {g.items.map((a) => (
                      <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5" data-testid={`school-doc-${a.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-zinc-800">{a.name}{a.required === false ? " (optional)" : ""}</p>
                          <p className="text-xs text-zinc-400">
                            Due {fmtDate(a.due_date)}
                            {a.uploaded_by_school ? " · uploaded by your school" : ""}
                            {a.rejection_reason ? ` · ${a.rejection_reason}` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {a.has_template && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/paperwork-assignments/${a.id}/template`} target="_blank" rel="noreferrer"
                              className="text-xs text-cc-blue inline-flex items-center gap-1 hover:underline" data-testid={`school-doc-blank-${a.id}`}>
                              <FileText className="w-3.5 h-3.5" />Blank form
                            </a>
                          )}
                          <StatusBadge status={a.status} />
                          {a.sign_in_app ? (
                            <span className="text-xs text-zinc-400 inline-flex items-center gap-1" data-testid={`school-doc-signonly-${a.id}`}>
                              <PenLine className="w-3.5 h-3.5" />Student signs this
                            </span>
                          ) : UPLOADABLE.includes(a.status) && (
                            <>
                              <input type="file" className="hidden" ref={(el) => (fileRefs.current[a.id] = el)} onChange={(e) => onFile(a, e)}
                                data-testid={`school-doc-input-${a.id}`} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                              <Button size="sm" variant="outline" className="h-8" disabled={busy === a.id}
                                onClick={() => fileRefs.current[a.id]?.click()} data-testid={`school-doc-upload-${a.id}`}>
                                {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />Upload</>}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
